import "./MapMarker.scss"

import React, { FC, memo, ReactNode } from "react"
import { Marker, OverlayView, OverlayViewF } from "@react-google-maps/api"

import markerDefault from "assets/img/map-marker-default.svg"
import markerAsset from "assets/img/map-marker-asset.svg"
import markerFixed from "assets/img/map-marker-fixed.svg"
import markerNode from "assets/img/map-marker-node.svg"
import markerTarget from "assets/img/map-marker-target.svg"
import markerHouse from "assets/img/map-marker-house.svg"
import markerPinYellow from "assets/img/map-marker-pin-yellow.svg"
import markerPinRed from "assets/img/map-marker-pin-red.svg"
import markerTrackingPlaceholder from "assets/img/map-marker-tracking-placeholder.svg"

import { Coordinates } from "@/types"

export enum MapMarkerType {
    default = "default",
    asset = "asset",
    fixed = "fixed",
    node = "node",
    target = "target",
    house = "house",
    pinYellow = "pinYellow",
    pinRed = "pinRed",
    tracking = "tracking",
}

export interface IMapMarkerProps {
    /* we use the index property as z-index to make possible to find the source marker props in the MapClusterer,
    should start from 1 */
    index?: number,
    coordinates: Coordinates
    type?: MapMarkerType
    title?: ReactNode
    clusterer?: any
    isInCluster?: boolean
    isDraggable?: boolean
    content?: React.ReactNode
    onClick?(): void
    onDrag?(coordinates: Coordinates): void
    onDragEnd?(coordinates: Coordinates): void

    // @TODO: refactor? the next options are used outside
    isClusterable?: boolean
}

const MapMarker: FC<React.PropsWithChildren<IMapMarkerProps>> = ({
    coordinates,
    index,
    type = MapMarkerType.default,
    title,
    clusterer,
    isInCluster,
    isDraggable,
    content,
    onClick,
    onDrag,
    onDragEnd,
}) => {

    // @TODO: can't move this out of the component because of the error:
    // Uncaught ReferenceError: google is not defined
    const MapMarkerParams: Record<MapMarkerType, {
        icon?: string | google.maps.Icon | google.maps.Symbol
        getTitleOffset?: () => { x: number, y: number }
    }> = {
        [MapMarkerType.default]: {
            icon: {
                url: markerDefault
            },
            getTitleOffset: () => ({ x: 18, y: -43 })
        },
        [MapMarkerType.asset]: {
            icon: {
                url: markerAsset
            },
            getTitleOffset: () => ({ x: 18, y: -43 })
        },
        [MapMarkerType.fixed]: {
            icon: {
                url: markerFixed
            },
            getTitleOffset: () => ({ x: 18, y: -43 })
        },
        [MapMarkerType.node]: {
            icon: {
                url: markerNode
            },
            getTitleOffset: () => ({ x: 18, y: -43 })
        },
        [MapMarkerType.target]: {
            icon: {
                url: markerTarget,
                anchor: new google.maps.Point(11, 24)
            },
            getTitleOffset: () => ({ x: 18, y: -25 })
        },
        [MapMarkerType.house]: {
            icon: {
                url: markerHouse,
                anchor: new google.maps.Point(11, 24)
            },
            getTitleOffset: () => ({ x: 18, y: -25 })
        },
        [MapMarkerType.pinYellow]: {
            icon: {
                url: markerPinYellow,
                anchor: new google.maps.Point(16, 16)
            },
            getTitleOffset: () => ({ x: 18, y: -25 })
        },
        [MapMarkerType.pinRed]: {
            icon: {
                url: markerPinRed,
                anchor: new google.maps.Point(16, 16)
            },
            getTitleOffset: () => ({ x: 18, y: -25 })
        },
        [MapMarkerType.tracking]: {
            icon: {
                url: markerTrackingPlaceholder,
                anchor: new google.maps.Point(24, 24)
            },
            getTitleOffset: () => ({ x: -24, y: -20 })
        },
    }

    function handleDragEnd(e: google.maps.MapMouseEvent) {
        if (typeof onDragEnd === "function" && e.latLng) {
            onDragEnd({
                lat: e.latLng.lat(),
                lng: e.latLng.lng()
            })
        }
    }

    function handleDrag(e: google.maps.MapMouseEvent) {
        if (typeof onDrag === "function" && e.latLng) {
            onDrag({
                lat: e.latLng.lat(),
                lng: e.latLng.lng()
            })
        }
    }

    return (
        <div className="map-marker">
            <Marker
                position={coordinates}
                icon={MapMarkerParams[type].icon}
                clusterer={clusterer}
                clickable={!!onClick}
                onClick={onClick}
                draggable={isDraggable}
                onDrag={handleDrag}
                onDragEnd={handleDragEnd}
                zIndex={index}
            />
            {!isInCluster && title &&
                <OverlayViewF
                    position={coordinates}
                    mapPaneName={OverlayView.OVERLAY_LAYER}
                    getPixelPositionOffset={MapMarkerParams[type].getTitleOffset}>
                    <div className={`map-marker__title map-marker__title--type-${type}`}>
                        <div className="map-marker__title-label clamp-2 word-break-break-word"
                            style={{ zIndex: index }}>
                            {title}
                        </div>
                    </div>
                </OverlayViewF>
            }
        </div>
    )
}

export default memo(MapMarker)
